import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPaperPlane, FaSearch, FaStar } from 'react-icons/fa';
import WebsiteTopBar from '../../components/WebsiteTopBar';
import CommonService from '../../services/common.service';
import { LOGISTICS_TRANSPORT_MODES } from '../../constants/logisticsProfileFormOptions';
import {
  clearLogisticsRfqPendingSubmit,
  loadLogisticsRfqPendingSubmit,
} from '../../utils/logisticsRfqPendingSubmit';
import { setLogisticsRfqSubmittedSessionFlag } from './logisticsRfqBrowseFlags';
import {
  completeP2pRfqLinkAfterModuleCreate,
  readStoredP2pLaunchContext,
} from '../../utils/p2pRfqLinkAfterCreate';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/website-top-bar.scss';
import '../../assets/styles/logistics_rfq_match_providers.scss';

const PAGE_SIZE = 12;

const RELOCATION_SCOPE_LABELS = {
  office: 'Office relocation',
  employee: 'Employee relocation',
  both: 'Office & employee relocation',
};

const RFQ_KIND_CONFIG = {
  generic: {
    formPath: '/logistics/rfq/generic',
    providersPath: '/logistics/rfq/generic/providers',
    title: 'Matched logistics providers',
    noMatchMessage: 'No matching logistics providers found.',
    loadingMessage: 'Finding matching logistics providers…',
    backLabel: '← Back to RFQ form',
    returnLabel: '← Back to matched providers',
    servicesKey: null,
    servicesTagClass: '',
  },
  'business-relocation': {
    formPath: '/logistics/rfq/business-relocation',
    providersPath: '/logistics/rfq/business-relocation/providers',
    title: 'Matched business relocation providers',
    noMatchMessage: 'No matching business relocation providers found.',
    loadingMessage: 'Finding business relocation providers…',
    backLabel: '← Back to business relocation form',
    returnLabel: '← Back to matched providers',
    servicesKey: 'relocationServices',
    servicesTagClass: 'lrfq-match-tag--relocation',
  },
  'exhibition-logistics': {
    formPath: '/logistics/rfq/exhibition-logistics',
    providersPath: '/logistics/rfq/exhibition-logistics/providers',
    title: 'Matched exhibition logistics providers',
    noMatchMessage: 'No matching exhibition logistics providers found.',
    loadingMessage: 'Finding exhibition logistics providers…',
    backLabel: '← Back to exhibition logistics form',
    returnLabel: '← Back to matched providers',
    servicesKey: 'exhibitionServices',
    servicesTagClass: 'lrfq-match-tag--exhibition',
  },
};

function transportModeLabels(modeIds) {
  if (!Array.isArray(modeIds)) return [];
  return modeIds.map((id) => {
    const m = LOGISTICS_TRANSPORT_MODES.find((x) => x.id === String(id).toLowerCase());
    return m ? m.label : String(id);
  });
}

function buildProvidersPath(basePath, companyIdParam) {
  return companyIdParam != null ? `${basePath}?companyId=${companyIdParam}` : basePath;
}

export default function LogisticsRfqMatchProvidersPage({ rfqKind = 'generic' }) {
  const config = RFQ_KIND_CONFIG[rfqKind] || RFQ_KIND_CONFIG.generic;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyIdParam = useMemo(() => {
    const raw = searchParams.get('companyId');
    const n = raw != null ? parseInt(String(raw), 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);

  const [pending, setPending] = useState(null);
  const [items, setItems] = useState([]);
  const [criteria, setCriteria] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const providersReturnPath = useMemo(
    () => buildProvidersPath(config.providersPath, companyIdParam),
    [config.providersPath, companyIdParam]
  );

  const profileLinkState = useMemo(
    () => ({
      returnTo: providersReturnPath,
      returnLabel: config.returnLabel,
    }),
    [providersReturnPath, config.returnLabel]
  );

  const runMatch = useCallback(
    async (payload, forceId) => {
      const res = await CommonService.post('/company/logistics-providers/rfq/match', {
        payload,
        rfqKind,
        forceIncludeCompanyId: forceId,
      });
      if (!res?.success) {
        throw new Error(res?.message || 'Could not match providers');
      }
      return res.data;
    },
    [rfqKind]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = loadLogisticsRfqPendingSubmit(rfqKind);
      if (!stored?.payload) {
        navigate(config.formPath, { replace: true });
        return;
      }
      if (cancelled) return;
      setPending(stored);

      try {
        const data = stored.matchResult
          ? stored.matchResult
          : await runMatch(stored.payload, companyIdParam ?? stored.companyIdParam ?? null);
        if (cancelled) return;

        const matchedItems = data?.items || [];
        setItems(matchedItems);
        setCriteria(data?.criteria || null);

        if (matchedItems.length === 0) {
          toast.info(config.noMatchMessage);
          clearLogisticsRfqPendingSubmit(rfqKind);
          navigate('/logistics/my-rfqs', { replace: true });
          return;
        }

        const preselect = new Set();
        const forceId = companyIdParam ?? stored.companyIdParam;
        if (forceId != null) {
          const hit = matchedItems.find((p) => p.companyId === forceId);
          if (hit) preselect.add(forceId);
        }
        setSelectedIds(preselect);
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.response?.data?.message || err?.message || 'Could not load matched providers.');
          navigate(config.formPath, { replace: true });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyIdParam, config.formPath, config.noMatchMessage, navigate, rfqKind, runMatch]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => {
      const name = (p.companyName || '').toLowerCase();
      const region = (p.serviceRegions || '').toLowerCase();
      const modes = transportModeLabels(p.transportModes).join(' ').toLowerCase();
      const specialized = config.servicesKey ? (p[config.servicesKey] || []).join(' ').toLowerCase() : '';
      return name.includes(q) || region.includes(q) || modes.includes(q) || specialized.includes(q);
    });
  }, [config.servicesKey, items, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const toggleSelect = (companyId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  };

  const sendRequest = async () => {
    if (!pending?.payload || selectedIds.size === 0) {
      toast.error('Select at least one logistics provider.');
      return;
    }
    setSending(true);
    try {
      const res = await CommonService.post('/company/logistics-providers/rfq/generic/batch', {
        payload: pending.payload,
        rfqKind,
        logisticsProviderCompanyIds: Array.from(selectedIds),
      });
      if (!res?.success) {
        toast.error(res?.message || 'Could not send request.');
        return;
      }
      const data = res?.data || {};
      const successCount = data.successCount ?? 0;
      const skippedCount = data.skippedCount ?? 0;
      const requiresApproval = data.requires_approval === true || data.approval?.mode === 'pending';
      const message =
        data.message ||
        (requiresApproval
          ? 'Logistics RFQ submitted for approval. It will be sent to providers after approval completes.'
          : skippedCount > 0
            ? 'RFQ sent successfully to new providers. Some selected providers already received this RFQ.'
            : 'RFQ sent successfully.');

      setLogisticsRfqSubmittedSessionFlag();
      clearLogisticsRfqPendingSubmit(rfqKind);

      const firstCreated = (data.submissions || []).find((s) => s.status === 'created' && s.id);
      const p2pLaunch = readStoredP2pLaunchContext();
      const linkedToP2p =
        firstCreated?.id && p2pLaunch?.p2p_transaction_id
          ? await completeP2pRfqLinkAfterModuleCreate({
              searchParams,
              sourceModule: 'logistics',
              sourceRfqId: firstCreated.id,
              sourceRfqCode: firstCreated.rfqReferenceNumber || null,
              sourceRfqTitle: pending?.payload?.companyName
                ? `Logistics RFQ — ${pending.payload.companyName}`
                : 'Logistics RFQ',
              sourceRfqStatus: requiresApproval ? 'pending_approval' : 'approved',
              sourceUrl: firstCreated.id
                ? `/logistics-providers/rfq/view?id=${firstCreated.id}`
                : null,
              navigate,
              p2pLaunchContext: p2pLaunch,
            })
          : false;

      toast.success(message);

      if (linkedToP2p) {
        return;
      }

      navigate('/logistics/my-rfqs', {
        replace: true,
        state: {
          rfqSubmittedMessage: requiresApproval
            ? 'Your logistics RFQ is pending approval and will appear in sent lists after approvers complete the workflow.'
            : skippedCount > 0
              ? `Your RFQ was sent to ${successCount} new provider${successCount === 1 ? '' : 's'}. ${skippedCount} already had this RFQ.`
              : `Your RFQ was sent to ${successCount} provider${successCount === 1 ? '' : 's'}.`,
        },
      });
    } catch (err) {
      const batchResult = err?.response?.data?.data;
      const msg = err?.response?.data?.message || err?.message;
      if (
        batchResult &&
        batchResult.successCount === 0 &&
        batchResult.skippedCount > 0 &&
        batchResult.failedCount === 0
      ) {
        toast.error(msg || 'All selected providers were already sent this RFQ.');
        return;
      }
      toast.error(typeof msg === 'string' ? msg : 'Send request failed.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="lrfq-match-page">
        <WebsiteTopBar />
        <main className="lrfq-match-main">
          <p className="text-secondary">{config.loadingMessage}</p>
        </main>
      </div>
    );
  }

  const criteriaLabel = criteria?.transportMode
    ? LOGISTICS_TRANSPORT_MODES.find((m) => m.id === criteria.transportMode)?.label || criteria.transportMode
    : null;
  const scopeLabel = criteria?.relocationScope
    ? RELOCATION_SCOPE_LABELS[criteria.relocationScope] || criteria.relocationScope
    : null;
  const criteriaParts = [criteriaLabel, scopeLabel, criteria?.industry].filter(Boolean);

  return (
    <div className="lrfq-match-page">
      <WebsiteTopBar />
      <main className="lrfq-match-main">
        <header className="lrfq-match-header">
          <Link to={config.formPath} className="lrfq-match-back">
            {config.backLabel}
          </Link>
          <h1 className="h3 fw-bold text-dark mb-1">{config.title}</h1>
          <p className="text-secondary mb-0">
            {items.length} provider{items.length === 1 ? '' : 's'} match your requirements
            {criteriaParts.length > 0 ? ` (${criteriaParts.join(' · ')})` : ''}. Select one or more, then send your
            request.
          </p>
        </header>

        <div className="lrfq-match-toolbar">
          <div className="lrfq-match-search input-group">
            <span className="input-group-text">
              <FaSearch aria-hidden />
            </span>
            <input
              type="search"
              className="form-control"
              placeholder="Search providers…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search matched providers"
            />
          </div>
          <span className="text-muted small">
            {selectedIds.size} selected · Page {page} of {totalPages}
          </span>
        </div>

        {filtered.length === 0 ? (
          <div className="lrfq-match-empty">
            <p className="mb-2">No providers match your search.</p>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setSearch('')}>
              Clear search
            </button>
          </div>
        ) : (
          <div className="lrfq-match-grid">
            {pageItems.map((p) => {
              const selected = selectedIds.has(p.companyId);
              const modes = transportModeLabels(p.transportModes);
              const specializedServices = config.servicesKey ? p[config.servicesKey] || [] : [];
              return (
                <button
                  key={p.companyId}
                  type="button"
                  className={`lrfq-match-card${selected ? ' lrfq-match-card--selected' : ''}`}
                  onClick={() => toggleSelect(p.companyId)}
                  aria-pressed={selected}
                >
                  <div className="lrfq-match-card__head">
                    <h2 className="lrfq-match-card__title">{p.companyName}</h2>
                    <span className="lrfq-match-card__check" aria-hidden>
                      {selected ? '✓' : ''}
                    </span>
                  </div>
                  {p.rating != null ? (
                    <p className="lrfq-match-card__meta mb-1">
                      <FaStar className="text-warning me-1" aria-hidden />
                      {p.rating.toFixed(1)}
                      {p.reviewCount != null ? ` (${p.reviewCount} reviews)` : ''}
                    </p>
                  ) : null}
                  {p.experienceLabel ? (
                    <p className="lrfq-match-card__meta">Experience: {p.experienceLabel}</p>
                  ) : null}
                  <p className="lrfq-match-card__meta">Regions: {p.serviceRegions}</p>
                  <p className="lrfq-match-card__meta">{p.responseTimeLabel}</p>
                  {modes.length > 0 ? (
                    <div className="lrfq-match-card__tags">
                      {modes.map((label) => (
                        <span key={label} className="lrfq-match-tag">
                          {label}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {specializedServices.length > 0 ? (
                    <div className="lrfq-match-card__tags mt-2">
                      {specializedServices.map((label) => (
                        <span
                          key={label}
                          className={`lrfq-match-tag${config.servicesTagClass ? ` ${config.servicesTagClass}` : ''}`}
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {p.matchReasons?.length > 0 ? (
                    <p className="lrfq-match-card__meta mt-2 mb-0">Match: {p.matchReasons.join(', ')}</p>
                  ) : null}
                  <Link
                    to={`/logistics-providers/profile/${p.companyId}`}
                    state={profileLinkState}
                    className="small fw-semibold mt-2 align-self-start"
                    onClick={(e) => e.stopPropagation()}
                  >
                    View profile →
                  </Link>
                </button>
              );
            })}
          </div>
        )}

        {totalPages > 1 ? (
          <nav className="d-flex justify-content-center gap-2 mt-3" aria-label="Pagination">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </nav>
        ) : null}

        <footer className="lrfq-match-footer">
          <span className="text-secondary">
            {selectedIds.size} provider{selectedIds.size === 1 ? '' : 's'} selected
          </span>
          <button
            type="button"
            className="lrfq-match-send-btn"
            disabled={sending || selectedIds.size === 0}
            onClick={sendRequest}
          >
            <FaPaperPlane aria-hidden />
            {sending ? 'Sending…' : 'Send Request'}
          </button>
        </footer>
      </main>
    </div>
  );
}
